import React, { useState, useEffect } from 'react';
import { Card, Table, Badge } from 'react-bootstrap';
import studentApplicationService from '@/shared/services/studentApplicationService';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';
import StatusPill from '@/components/common/StatusPill';
import LoadError from '@/components/common/LoadError';
import LoadingState from '@/components/common/LoadingState';
import FacultyHiringCard from '@/features/positions/FacultyHiringCard';

const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });

function LaApplicantsCard({ term }) {
    // With hiring on, applicants are reviewed per position on My Positions,
    // so this card lists them by section from there and reads no student
    // applications.
    const hiringEnabled = useFeature('hiring');
    // This card reads the student application API, which is gated by the same
    // flag. With the flag off the server answers 404, and 404 is not the 403
    // the catch below treats as "hide me", so the card would render its
    // load-failed state for a feature that does not exist yet.
    const studentApplicationEnabled = useFeature('studentApplication');
    const [applicants, setApplicants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hidden, setHidden] = useState(false);
    const [errored, setErrored] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!term || !studentApplicationEnabled || hiringEnabled) {
            setLoading(false);
            setHidden(true);
            return undefined;
        }

        let active = true;
        setLoading(true);
        setHidden(false);
        setErrored(false);

        studentApplicationService
            .getApplicationsByProgram('la', { term })
            .then((data) => {
                if (active) setApplicants(data ?? []);
            })
            .catch((err) => {
                if (!active) return;
                // 403 = not authorized (visibility off / no access) → hide the
                // card entirely. Any other error (network / 5xx) is transient,
                // so keep the card and show a load-failed state instead of
                // silently vanishing on an authorized faculty member.
                if (err?.response?.status === 403) {
                    setHidden(true);
                } else {
                    setErrored(true);
                }
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [term, studentApplicationEnabled, hiringEnabled, attempt]);

    if (hiringEnabled) {
        return <FacultyHiringCard term={term} />;
    }

    if (loading) {
        return <LoadingState rows={1} label="Loading applicants" />;
    }

    if (hidden) {
        return null;
    }

    // Option B: no card when there are simply no applicants (an authorized
    // faculty with an empty list). The card only appears for real data or to
    // surface a load error.
    if (applicants.length === 0 && !errored) {
        return null;
    }

    return (
        <Card className="border-0 shadow-sm mt-4">
            <Card.Header className="bg-transparent border-0">
                <h2 className="h5 mb-0">LA Applicants for Your Courses</h2>
            </Card.Header>
            <Card.Body className="p-0">
                {errored ? (
                    <LoadError
                        what="the LA applicants"
                        onRetry={() => setAttempt((n) => n + 1)}
                        className="m-3"
                    />
                ) : (
                    <Table responsive hover className="mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th>Student Name</th>
                                <th>Email</th>
                                <th>Course Preferences</th>
                                <th>Submitted</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {applicants.map((app) => (
                                <tr key={app._id}>
                                    <td>
                                        <strong>
                                            {app.student?.name || 'Unknown'}
                                        </strong>
                                    </td>
                                    <td>
                                        <small className="text-muted">
                                            {app.student?.email || '-'}
                                        </small>
                                    </td>
                                    <td>
                                        {app.coursePreferences?.length > 0
                                            ? app.coursePreferences.map(
                                                  (pref, idx) => (
                                                      <Badge
                                                          key={idx}
                                                          bg="secondary"
                                                          className="me-1 mb-1"
                                                      >
                                                          {pref.course
                                                              ?.subject ||
                                                              ''}{' '}
                                                          {pref.course
                                                              ?.courseNumber ||
                                                              ''}
                                                      </Badge>
                                                  )
                                              )
                                            : '-'}
                                    </td>
                                    <td>
                                        {app.submittedAt
                                            ? formatDate(app.submittedAt)
                                            : '-'}
                                    </td>
                                    <td>
                                        <StatusPill
                                            family="studentApplication"
                                            value={app.status}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );
}

export default LaApplicantsCard;
